/**
 * DemoLoginPage — the seeded demo experience, isolated at /demo.
 * Pre-fills demo credentials and labels itself clearly so nobody mistakes it
 * for their real account. Real users never land here except via the small
 * "Try the interactive demo" link on the login page.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';
import { authApi } from '../utils/api';

export default function DemoLoginPage() {
  const navigate = useNavigate();
  const email = 'demo@riggingpro.ai';
  const password = 'demo123';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await authApi.login(email, password);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Demo login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <Shield className="h-16 w-16 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Conformara Demo</h1>
          <p className="text-gray-600 mt-2">
            Loaded with sample data (Summit Tower Solutions) so you can explore every feature.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-amber-300">
          <form onSubmit={handleSubmit} className="space-y-6">
            <p className="text-gray-700">No credentials needed. Explore sample data in the demo company.</p>

            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Explore the Demo'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-gray-600">
          Have a real account?{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}
